/**
 * Logger Context Provider
 *
 * Provides logger with automatic context enrichment throughout the app.
 * Automatically injects userId, workspaceId, sessionId, and route into all logs.
 *
 * Usage:
 *   // Wrap app in provider
 *   <LoggerProvider>
 *     <App />
 *   </LoggerProvider>
 *
 *   // Use in components
 *   const { logger, logAPI, logUserAction } = useLogger();
 *   logger.info({ action: 'click' }, 'Button clicked');
 *   logUserAction('submit_form', { formId: 'contact' });
 */

import React, { createContext, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  logger as baseLogger,
  logAPI as baseLogAPI,
  logUserAction as baseLogUserAction,
  logNavigation as baseLogNavigation,
  logPerformance as baseLogPerformance,
  logError as baseLogError,
  LogContext,
  installGlobalErrorHandlers,
} from '@/services/logger';

// ============================================================================
// TYPES
// ============================================================================

interface LoggerContextValue {
  // Main logger
  logger: typeof baseLogger;

  // Helper loggers
  logAPI: typeof baseLogAPI;
  logUserAction: typeof baseLogUserAction;
  logNavigation: typeof baseLogNavigation;
  logPerformance: typeof baseLogPerformance;
  logError: typeof baseLogError;

  // Context enrichment
  enrichContext: (context?: LogContext) => LogContext;
}

interface LoggerProviderProps {
  children: React.ReactNode;
  userId?: string;
  workspaceId?: string;
  sessionId?: string;
}

// ============================================================================
// CONTEXT
// ============================================================================

const LoggerContext = createContext<LoggerContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function LoggerProvider({
  children,
  userId,
  workspaceId,
  sessionId,
}: LoggerProviderProps) {
  const location = useLocation();

  // Install global error handlers once
  useEffect(() => {
    installGlobalErrorHandlers();
  }, []);

  /**
   * Enrich context with user, workspace, and route information.
   */
  const enrichContext = useCallback(
    (context: LogContext = {}): LogContext => {
      return {
        userId,
        workspaceId,
        sessionId,
        route: location.pathname,
        ...context,
      };
    },
    [userId, workspaceId, sessionId, location.pathname]
  );

  /**
   * Create enriched logger methods.
   */
  const logger = useMemo(() => ({
    debug: (context: LogContext, message: string) => {
      baseLogger.debug(enrichContext(context), message);
    },
    info: (context: LogContext, message: string) => {
      baseLogger.info(enrichContext(context), message);
    },
    warn: (context: LogContext, message: string) => {
      baseLogger.warn(enrichContext(context), message);
    },
    error: (context: LogContext, message: string) => {
      baseLogger.error(enrichContext(context), message);
    },
    fatal: (context: LogContext, message: string) => {
      baseLogger.fatal(enrichContext(context), message);
    },
  }), [enrichContext]);

  /**
   * Create enriched logAPI methods.
   */
  const logAPI = useMemo(() => ({
    request: (method: string, url: string, context: LogContext = {}) => {
      baseLogAPI.request(method, url, enrichContext(context));
    },
    response: (
      method: string,
      url: string,
      status: number,
      durationMs: number,
      context: LogContext = {}
    ) => {
      baseLogAPI.response(method, url, status, durationMs, enrichContext(context));
    },
    error: (method: string, url: string, error: Error, context: LogContext = {}) => {
      baseLogAPI.error(method, url, error, enrichContext(context));
    },
  }), [enrichContext]);

  /**
   * Create enriched logUserAction method.
   */
  const logUserAction = useCallback(
    (action: string, context: LogContext = {}) => {
      baseLogUserAction(action, enrichContext(context));
    },
    [enrichContext]
  );

  /**
   * Create enriched logNavigation method.
   */
  const logNavigation = useCallback(
    (from: string, to: string, context: LogContext = {}) => {
      baseLogNavigation(from, to, enrichContext(context));
    },
    [enrichContext]
  );

  /**
   * Create enriched logPerformance methods.
   */
  const logPerformance = useMemo(() => ({
    render: (component: string, durationMs: number, context: LogContext = {}) => {
      baseLogPerformance.render(component, durationMs, enrichContext(context));
    },
    fetch: (resource: string, durationMs: number, context: LogContext = {}) => {
      baseLogPerformance.fetch(resource, durationMs, enrichContext(context));
    },
  }), [enrichContext]);

  /**
   * Create enriched logError methods.
   */
  const logError = useMemo(() => ({
    exception: (message: string, error: Error, context: LogContext = {}) => {
      baseLogError.exception(message, error, enrichContext(context));
    },
    validation: (field: string, value: any, reason: string, context: LogContext = {}) => {
      baseLogError.validation(field, value, reason, enrichContext(context));
    },
  }), [enrichContext]);

  const value: LoggerContextValue = {
    logger,
    logAPI,
    logUserAction,
    logNavigation,
    logPerformance,
    logError,
    enrichContext,
  };

  return (
    <LoggerContext.Provider value={value}>
      {children}
    </LoggerContext.Provider>
  );
}

